import { ReadingModesService } from 'src/app/services/reading-modes.service';
import { AnnotationEvent } from './../../../models/Events';
import { PdfInstanceManagerService } from 'src/app/services/instance/pdf-instance-manager.service';
import { AsgardDocPdf } from 'src/app/core/documents/asgard-doc-pdf';
import { WordPredictionService } from './../word-prediction/word-prediction.service';
import { Injectable, NgZone } from '@angular/core';
import { ReadingService } from '../text/reading.service';
import { SettingsService } from '../settings/settings.service';
import { DocumentHighlightService } from './document-highlight.service';
import { ReadingActions } from 'src/app/core/enums';
import { ReadingMode } from 'src/app/core/reading-utils/enums';
import { SpellCheckService } from '../spell-check/spell-check.service';
import { ISettings, ViewerInstanceClickEv } from 'src/app/core/interfaces';
import { DocumentManagerService } from './document-manager.service';
import { AsgardDocBase } from 'src/app/core/documents/asgard-doc-base';
import { ToastrActionEnum, ToastrStringsEnum } from 'src/app/utils/enums';
import { ToastrDialogService } from '../toastr-dialog.service';
import { CustomTextareaService } from '../text-notes/custom-textarea.service';
// import { MarkersBarService } from '../markers-bar.service';
import { ChainResponsibilityService } from '../chain-responsibility/chain-responsibility.service';
import { ChainSenders } from '../chain-responsibility/chain.interface';
import { AudioMakerService } from '../audio-maker/audio-maker.service';
import { AudiomakerDialogComponent } from '../../components/dialogs/audiomaker-dialog/audiomaker-dialog.component';
import { ModalWindowService } from '../ui/modal-window.service';
import { TabService } from '../tab.service';
import { AsgardDocHtml } from 'src/app/core/documents/asgard-doc-html';
import { DocumentMarkersService } from './document-markers.service';
import { DocumentMarkersMode } from 'src/app/core/pdf-utils/enums';
import { Core } from '@pdftron/webviewer';


@Injectable({
  providedIn: 'root'
})
export class DocumentMouseHandlerService {
  private get settings(): ISettings { return this.settingsService.settings; }
  private get currentDoc(): AsgardDocBase | null | undefined { return this.docMgrService.currentDoc; }

  constructor(
    private settingsService: SettingsService,
    private docHighlight: DocumentHighlightService,
    private speelCheckService: SpellCheckService,
    private wordPredictionService: WordPredictionService,
    private toastrService: ToastrDialogService,
    private readonly pdfInstanceManager: PdfInstanceManagerService,
    private readonly ngZone: NgZone,
    private readonly docMgrService: DocumentManagerService,
    private readonly readingService: ReadingService,
    // private readonly markersBarService: MarkersBarService,
    private area: CustomTextareaService,
    private readonly readingModeService: ReadingModesService,
    private readonly chain: ChainResponsibilityService,
    private readonly audioMakerService: AudioMakerService,
    private readonly documentMarkerService: DocumentMarkersService,
    private modalWindowService: ModalWindowService,
    private tabService: TabService
  ) {
    this.initSubscriptions();
  }

  handleDocumentClickedEvent = async (e: MouseEvent, preventReading: boolean = false) => {
    if (this.currentDoc instanceof AsgardDocPdf) {
      this.area.commonTextNotesClicksHandler(e);
    }

    if (preventReading || this.documentMarkerService.currentMode !== DocumentMarkersMode.None) {
      return;
    }

    if (this.currentDoc instanceof AsgardDocPdf) {
      const currentPageHasText = await this.currentDoc.currentPageHasText();
      if (!currentPageHasText) {
        this.ngZone.run(() => {
          this.toastrService.showToastrByType(ToastrActionEnum.Info, ToastrStringsEnum.PageHasNoText, false);
        });
        return;
      }
    }

    if (this.currentDoc && (this.readingModeService.readingMode === ReadingMode.ClickToRead ||
      this.readingModeService.readingMode === ReadingMode.ClickToPlay)) {
      await this.currentDoc.selectReadingUnitByClick(
        e, this.settings.readingUnit,
        true,
      );
    }

    if (this.readingModeService.readingMode === ReadingMode.ClickToRead) {
      this.runReadingServiceAction(ReadingActions.PlayPause);
    }
  }

  async handleHoverEvent(e: MouseEvent): Promise<void> {
    this.runReadingServiceAction(ReadingActions.Stop);

    if (this.currentDoc instanceof AsgardDocPdf) {
      const currentPageHasText = await this.currentDoc.currentPageHasText();
      if (!currentPageHasText) {
        this.ngZone.run(() => {
          this.toastrService.showToastrByType(ToastrActionEnum.Info, ToastrStringsEnum.PageHasNoText, false);
        });
        return;
      }
    }

    if (this.currentDoc) {
      await this.currentDoc.selectHoverWord(e);
    }

    if (this.readingModeService.readingMode === ReadingMode.Hover) {
      this.runReadingServiceAction(ReadingActions.PlayPause);
    }
  }

  mouseLeftDown(): void {
    this.runReadingServiceAction(ReadingActions.Stop);
    this.docHighlight.cleanCurrentHighlight(true, true);
  }

  mouseLeftUp(): void {
    if (this.audioMakerService.isActive()) {
      this.ngZone.run(() => {
        if (this.tabService.activeTab) {
          const voice = this.tabService.getTabVoice(this.tabService.activeTab.id);
          if (voice) {
            this.audioMakerService.getAudioBlob(this.getSelectedText(), voice).then(x =>
              this.modalWindowService.createModalWindow(AudiomakerDialogComponent, { file: x })
            );
          }
        }
      });
      /* TODO
      - get selected text
      - convert text to mp3
      - show dialog
      - where does above code go?
      - where does error handling go?
      */
    } else if (this.currentDoc && this.readingModeService.readingMode === ReadingMode.Selection &&
      this.currentDoc.isDocumentReady && this.currentDoc.hasSelection()) {
      this.currentDoc.setSelectionToReadingItem().then(() => this.runReadingServiceAction(ReadingActions.PlayPause));
    }
  }

  handleClickSpellcheckerDialog(): void {
    if (this.currentDoc && this.currentDoc instanceof AsgardDocHtml && (this.currentDoc as AsgardDocHtml).isEditable) {
      this.speelCheckService.onIncorrectWordClickedHandler();
    }
  }

  handleClickToWordForWordPrediction(): void {
    if (this.currentDoc && this.currentDoc instanceof AsgardDocHtml && (this.currentDoc as AsgardDocHtml).isEditable) {
      this.wordPredictionService.onWordClickedHandler();
    }
  }

  // convertAnnotationToEditableElement(e: Annotations.Annotation): void {
  //   this.area.convertAnnotationToEditableElement(e);
  // }

  commonTextNotesClicksHandler(e: MouseEvent | null = null): void {
    this.area.commonTextNotesClicksHandler(e);
  }

  mouseInAndMoving(): void {
    const textLength = this.getSelectedText().length;
    this.audioMakerService.updateCurrentCharacterCount(textLength);
  }

  private runReadingServiceAction(action: ReadingActions): void {
    this.ngZone.run(() => this.readingService.runAction(action));
  }

  private initSubscriptions(): void {
    this.pdfInstanceManager.documentScrolled.subscribe(() => {
      this.commonTextNotesClicksHandler(null);
    });

    this.pdfInstanceManager.mouseClicked.subscribe((e: ViewerInstanceClickEv) => {
      this.chain.handleClickEvent(ChainSenders.PDF_DOCUMENT, null, e.docId, e.pandeIndex, e.ev,
        this.handleDocumentClickedEvent, [e.ev, false]);
    });

    this.pdfInstanceManager.mouseLeftDown.subscribe(() => {
      this.mouseLeftDown();
    });

    this.pdfInstanceManager.mouseLeftUp.subscribe(() => {
      this.mouseLeftUp();
    });

    this.pdfInstanceManager.annotationDoubleClicked.subscribe((e: AnnotationEvent<Core.Annotations.Annotation>) => {
      if (e.annotations && e.annotations.length !== 0) {
        // console.log(e.annotations);
        this.area.convertAnnotationToEditableElement(e.annotations[0]);
      }
    });

    this.pdfInstanceManager.wordHovered.subscribe(e => {
      this.handleHoverEvent(e);
    });

    this.pdfInstanceManager.mouseInAndMoving.subscribe(() => {
      this.mouseInAndMoving();
    });
  }

  private getSelectedText(): string {
    if (this.currentDoc && this.currentDoc.isDocumentReady && this.currentDoc.hasSelection()) {
      return this.currentDoc.selectedText();
    }

    return '';
  }
}
